import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { SYMPTOM_CATALOG, DEFAULT_ENABLED_SYMPTOMS, getScoreLabel } from '../../utils/wellness';
import './DailyWellnessLog.css';

export default function DailyWellnessLog() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [client, setClient] = useState(null);
  const [enabledSymptoms, setEnabledSymptoms] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Symptom scores — keyed by symptom key
  const [scores, setScores] = useState({});
  const [journal, setJournal] = useState('');
  const [customAnswers, setCustomAnswers] = useState({});

  // Photo state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCategory, setPhotoCategory] = useState('food');
  const [photoCaption, setPhotoCaption] = useState('');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    setLoading(true);

    const { data: clientData } = await supabase
      .from('clients')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (!clientData) {
      setLoading(false);
      return;
    }
    setClient(clientData);

    // Load enabled symptoms for this client
    if (clientData.enabled_symptoms && Array.isArray(clientData.enabled_symptoms)) {
      setEnabledSymptoms(clientData.enabled_symptoms);
    }

    // Check for existing log today
    const { data: existingLog } = await supabase
      .from('daily_logs')
      .select('*, custom_answers(*)')
      .eq('client_id', clientData.id)
      .eq('date', today)
      .single();

    if (existingLog) {
      // Restore symptom scores
      const restored = {};
      for (const symptom of SYMPTOM_CATALOG) {
        if (existingLog[symptom.key] != null) {
          restored[symptom.key] = existingLog[symptom.key];
        }
      }
      setScores(restored);
      setJournal(existingLog.journal_text || '');

      // Restore custom answers
      const answers = {};
      (existingLog.custom_answers || []).forEach((a) => {
        answers[a.custom_question_id] = a.answer_value;
      });
      setCustomAnswers(answers);
    }

    // Load custom questions
    const { data: questions } = await supabase
      .from('custom_questions')
      .select('*')
      .eq('client_id', clientData.id)
      .eq('is_active', true)
      .order('sort_order');

    setCustomQuestions(questions || []);
    setLoading(false);
  }

  function updateScore(key, value) {
    setScores((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Build log payload with all symptom keys
      const logPayload = {
        client_id: client.id,
        date: today,
        journal_text: journal || null,
      };

      // Add each symptom score
      for (const symptom of SYMPTOM_CATALOG) {
        logPayload[symptom.key] = scores[symptom.key] != null ? parseInt(scores[symptom.key]) : null;
      }

      // Legacy fields — map from new symptoms for backward compatibility
      logPayload.energy = scores.energy != null ? parseInt(scores.energy) : null;
      logPayload.mood = scores.mood != null ? parseInt(scores.mood) : null;
      logPayload.digestion = scores.digestive_comfort != null ? parseInt(scores.digestive_comfort) : null;
      logPayload.sleep = scores.sleep_quality != null ? parseInt(scores.sleep_quality) : null;

      const { data: log, error: logError } = await supabase
        .from('daily_logs')
        .upsert(logPayload, { onConflict: 'client_id,date' })
        .select()
        .single();

      if (logError) throw logError;

      // Save custom answers
      if (log && Object.keys(customAnswers).length > 0) {
        await supabase.from('custom_answers').delete().eq('daily_log_id', log.id);
        const answerRows = Object.entries(customAnswers)
          .filter(([_, val]) => val !== '' && val != null)
          .map(([qId, val]) => ({
            daily_log_id: log.id,
            custom_question_id: qId,
            answer_value: val.toString(),
          }));
        if (answerRows.length > 0) {
          await supabase.from('custom_answers').insert(answerRows);
        }
      }

      // Upload photo if selected
      if (photoFile && log) {
        const fileExt = photoFile.name.split('.').pop();
        const filePath = `${client.id}/${today}_${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, photoFile);
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('photos')
            .getPublicUrl(filePath);
          await supabase.from('photos').insert({
            client_id: client.id,
            category: photoCategory,
            caption: photoCaption || null,
            photo_url: urlData.publicUrl,
            date: today,
          });
        }
      }

      setSaved(true);
      setTimeout(() => navigate('/client'), 1500);
    } catch (err) {
      setError(err.message || 'Failed to save log.');
      setSaving(false);
    }
  }

  function updateCustomAnswer(questionId, value) {
    setCustomAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="screen container">
        <div className="wlog-saved">
          <div className="wlog-saved-icon">✓</div>
          <h2>Log Saved</h2>
          <p>Your symptom log for today has been recorded.</p>
        </div>
      </div>
    );
  }

  // Filter to only show enabled symptoms
  const activeSymptoms = SYMPTOM_CATALOG.filter((s) => enabledSymptoms.includes(s.key));

  return (
    <div className="screen container wlog-screen">
      <button onClick={() => navigate(-1)} className="back-link">
        ← Back
      </button>

      <h1 className="wlog-title">Daily Symptom Log</h1>
      <p className="wlog-date">
        {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
      </p>

      <form className="wlog-form" onSubmit={handleSubmit}>
        {error && <div className="form-error">{error}</div>}

        {/* Symptom sliders */}
        <div className="symptom-sliders">
          {activeSymptoms.map((symptom) => {
            const val = scores[symptom.key];
            const hasValue = val != null && val !== '';
            return (
              <div key={symptom.key} className="symptom-slider-group">
                <div className="symptom-slider-header">
                  <span className="symptom-icon">{symptom.icon}</span>
                  <div className="symptom-label-wrap">
                    <span className="symptom-label">{symptom.label}</span>
                    {symptom.subtitle && (
                      <span className="symptom-subtitle">{symptom.subtitle}</span>
                    )}
                  </div>
                  <span className={`symptom-value ${hasValue ? 'has-value' : ''}`}>
                    {hasValue ? val : '—'}
                  </span>
                </div>
                <div className="symptom-slider-row">
                  <span className="slider-anchor">{symptom.low}</span>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={hasValue ? val : 5}
                    onChange={(e) => updateScore(symptom.key, parseInt(e.target.value))}
                    className="symptom-range"
                    style={{
                      '--slider-color': symptom.color,
                      '--slider-pct': `${(hasValue ? val : 5) * 10}%`,
                    }}
                  />
                  <span className="slider-anchor">{symptom.high}</span>
                </div>
                {hasValue && (
                  <div className="symptom-score-label" style={{ color: symptom.color }}>
                    {getScoreLabel(val)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Custom questions */}
        {customQuestions.length > 0 && (
          <div className="wlog-custom-section">
            <h3 className="wlog-section-label">From Your Practitioner</h3>
            {customQuestions.map((q) => (
              <div key={q.id} className="form-group">
                <label>{q.question_text}</label>
                {q.question_type === 'dropdown' ? (
                  <select
                    className="form-input"
                    value={customAnswers[q.id] || ''}
                    onChange={(e) => updateCustomAnswer(q.id, e.target.value)}
                  >
                    <option value="">Select...</option>
                    {(q.options || []).map((opt, i) => (
                      <option key={i} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : q.question_type === 'yes_no' ? (
                  <div className="wlog-yesno">
                    {['Yes', 'No'].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={`wlog-yesno-btn ${customAnswers[q.id] === opt ? 'wlog-yesno-active' : ''}`}
                        onClick={() => updateCustomAnswer(q.id, opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : q.question_type === 'scale' ? (
                  <div className="wlog-scale">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`wlog-scale-btn ${customAnswers[q.id] === n.toString() ? 'wlog-scale-active' : ''}`}
                        onClick={() => updateCustomAnswer(q.id, n.toString())}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Your answer..."
                    value={customAnswers[q.id] || ''}
                    onChange={(e) => updateCustomAnswer(q.id, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Journal */}
        <div className="form-group">
          <label>Journal / Notes</label>
          <textarea
            className="form-input"
            rows={4}
            placeholder="How was your day? Anything worth noting..."
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </div>

        {/* Photo upload */}
        <div className="wlog-photo-section">
          <h3 className="wlog-section-label">Photo (optional)</h3>
          <div className="wlog-photo-row">
            <select
              className="form-input"
              value={photoCategory}
              onChange={(e) => setPhotoCategory(e.target.value)}
              style={{ maxWidth: '140px' }}
            >
              <option value="food">Food</option>
              <option value="symptoms">Symptoms</option>
              <option value="progress">Progress</option>
            </select>
            <input
              type="file"
              accept="image/*"
              className="wlog-file-input"
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
            />
          </div>
          {photoFile && (
            <input
              type="text"
              className="form-input"
              placeholder="Caption (optional)"
              value={photoCaption}
              onChange={(e) => setPhotoCaption(e.target.value)}
              style={{ marginTop: '8px' }}
            />
          )}
        </div>

        <button type="submit" className="btn btn-primary wlog-submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Log'}
        </button>
      </form>
    </div>
  );
}
